import * as React from 'react';
import moment from 'moment';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { isSystemMessage } from 'utils/messenger';
import { t } from 'utils/language';

import SystemMessageOld from './messages/SystemMessage';
import SystemMessageWrapper from './system/v2/wrapper/SystemMessageWrapper';
import UserMessage from './messages/UserMessage';

import type { Campaign, MessengerMessage, Subcampaign } from 'types';

type Props = {
  messages: Array<MessengerMessage>,
  groupKey: string,
  campaign: Campaign,
  subcampaign: Subcampaign,
  subcampaignId: number,
  isBrand: boolean,
  latestMessageExternalIdForInfluencerAction: string | undefined | null
};

class MessagesByDate extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props): boolean {
    const {
      messages,
      groupKey,
      subcampaign,
      subcampaignId,
      latestMessageExternalIdForInfluencerAction
    } = this.props;

    return (
      !isEqual(nextProps.messages, messages) ||
      nextProps.groupKey !== groupKey ||
      nextProps.subcampaignId !== subcampaignId ||
      nextProps.subcampaign.state !== subcampaign.state ||
      nextProps.latestMessageExternalIdForInfluencerAction !== latestMessageExternalIdForInfluencerAction
    );
  }

  renderMessage(message: MessengerMessage): React.ReactNode {
    const {
      campaign,
      subcampaign,
      subcampaignId,
      isBrand,
      latestMessageExternalIdForInfluencerAction
    } = this.props;

    const systemMessageType = get(message, 'metadata.type', '').toLowerCase();
    const isPayoutRescheduledType = [
      'payout_rescheduled_business_and_bank_details_unverified',
      'payout_rescheduled_business_and_bank_details_incomplete',
      'payout_rescheduled_invoice_not_received',
      'payout_rescheduled_accidental_release',
      'payout_rescheduled_other'
    ].includes(systemMessageType);

    if (isSystemMessage(message)) {
      if (!campaign) {
        return null;
      }

      // new component (in preparation for upcoming messenger revamp)
      // please use this wrapper component to house all new system messages
      // also create all new child components following `SystemMessageNegotiationsReopened` as a guide
      // note: the `systemMessageType === 'xxx'` part is temporary until the actual messenger revamp happens
      if (
        systemMessageType === 'negotiation_reopen'
        || systemMessageType === 'ccm_campaign_ended'
        || systemMessageType === 'ccm_outfit_declined'
        || systemMessageType === 'ccm_campaign_participation'
        || (systemMessageType === 'payment_details_are_empty' && !isBrand)
        || isPayoutRescheduledType
      ) {
        return (
          <SystemMessageWrapper
            key={`system-message-${message.externalId}`}
            campaign={campaign}
            subcampaign={subcampaign}
            message={message}
            messageType={systemMessageType}
            influencerProfile={subcampaign.creatorProfile}
            isBrand={isBrand}
            latestMessageExternalIdForInfluencerAction={latestMessageExternalIdForInfluencerAction}
          />
        );
      }

      return (
        <SystemMessageOld
          key={`system-message-${message.externalId}`}
          message={message}
          campaign={campaign}
          subcampaign={subcampaign}
          subcampaignId={subcampaignId}
          influencerProfile={subcampaign.creatorProfile}
          latestMessageExternalIdForInfluencerAction={latestMessageExternalIdForInfluencerAction}
          isBrand={isBrand}
        />
      );
    }

    return (
      <UserMessage
        key={`user-message-${message.externalId}${message.isDeleted ? '-deleted' : ''}`}
        subcampaignId={subcampaignId}
        isBrand={isBrand}
        message={message}
      />
    );
  }

  render() {
    const {
      messages,
      groupKey
    } = this.props;

    const date = moment(groupKey, 'YYYYMMDD');
    const title = date.format('dddd MMMM D, YYYY');
    const isToday = moment().diff(date, 'days') === 0;

    return (
      <div className="messages-group">
        <div className="messages-group__title">
          <div className="messages-group-title__line" />
          <div className="messages-group-title__label">
            {isToday ? t('messenger.group_today_label') : title}
          </div>
          <div className="messages-group-title__line" />
        </div>
        <div className="messages-group__messages">
          {
            messages.map(message => this.renderMessage(message))
          }
        </div>
      </div>
    );
  }
}

export default MessagesByDate;
